import React, { useEffect, useState, useCallback, useRef } from 'react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import CameraCapture from '../../component/Panel/CameraCapture';
import LocationBadge from '../../component/Panel/LocationBadge';
import { FaUndoAlt, FaCalendarTimes, FaCheck } from 'react-icons/fa';
import axios from 'axios';
import IconRenderer from '../../component/Panel/IconRenderer';
import { formatDate01, formatTime } from '../../helper/helper';
import AuthImage from '../../component/common/AuthImage';

const getDistanceFromLatLonInMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Radius of the earth in meters
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in meters
};

const deg2rad = (deg) => deg * (Math.PI / 180);

const Absensi = ({ data }) => {
    const [capturedImage, setCapturedImage] = useState(null);
    const [ipClient, setIpClient] = useState();
    const [error, setError] = useState(null);
    const [locationAllowed, setLocationAllowed] = useState(false);
    const [userLokasi, setUserLokasi] = useState([]);
    const [activeTab, setActiveTab] = useState(0); // 0 = Masuk, 1 = Keluar
    const [absensiHariIni, setAbsensiHariIni] = useState(null);
    const [loadingAbsensi, setLoadingAbsensi] = useState(true);
    const [userLocation, setUserLocation] = useState({ latitude: null, longitude: null });
    const [locationName, setLocationName] = useState(null);
    const [distanceMeters, setDistanceMeters] = useState(null);
    /** Debug GPS: berapa kali update + akurasi */
    const [gpsDebug, setGpsDebug] = useState({ tick: 0, accuracy: null });
    const [isHoliday, setIsHoliday] = useState(false);
    const [isSunday, setIsSunday] = useState(false);
    const [holidayInfo, setHolidayInfo] = useState(null);

    // Refs for cleanup
    const pcRef = useRef(null);
    const timeoutRef = useRef(null);
    const retryCountRef = useRef(0);
    const isGettingIPRef = useRef(false);
    const userLocationRef = useRef({ latitude: null, longitude: null });
    const ipClientRef = useRef(null);
    const watchIdRef = useRef(null);
    const locationPollRef = useRef(null);
    const evaluateAccessRef = useRef(null);

    // Cookies are sent automatically with withCredentials: true
    // No need for Authorization header anymore

    const GEO_CACHE_KEY = 'absensi_last_geo';

    const handleCapture = async (imageSrc, result) => {
        setCapturedImage(imageSrc);
        setError(null);
        // Optimistic update supaya kamera langsung hilang (jangan tunggu dashboard)
        if (result?.waktuMasuk || result?.waktuKeluar) {
            setAbsensiHariIni((prev) => ({
                ...(prev || {}),
                ...result,
                waktuMasuk: result.waktuMasuk ?? prev?.waktuMasuk ?? null,
                fotoMasuk: result.fotoMasuk ?? prev?.fotoMasuk ?? null,
                waktuKeluar: result.waktuKeluar ?? prev?.waktuKeluar ?? null,
                fotoKeluar: result.fotoKeluar ?? prev?.fotoKeluar ?? null,
                latitudeMasuk: result.latitudeMasuk ?? prev?.latitudeMasuk ?? null,
                longitudeMasuk: result.longitudeMasuk ?? prev?.longitudeMasuk ?? null,
                locationNameMasuk: result.locationNameMasuk ?? prev?.locationNameMasuk ?? null,
                latitudeKeluar: result.latitudeKeluar ?? prev?.latitudeKeluar ?? null,
                longitudeKeluar: result.longitudeKeluar ?? prev?.longitudeKeluar ?? null,
                locationNameKeluar: result.locationNameKeluar ?? prev?.locationNameKeluar ?? null,
            }));
            // Setelah absen masuk → langsung ke tab Absen Pulang
            if (result.waktuMasuk && !result.waktuKeluar) {
                setActiveTab(1);
            }
        }
        await getAbsensiHariIni();
    };

    const handleError = (e) => {
        setCapturedImage(null); // Clear the captured image on error
        setError(e);
    };

    const ipPrefixMatch = (clientIp, jaringan) => {
        if (!clientIp || !jaringan) return false;
        const networkPrefix = jaringan.split('.').slice(0, 3).join('.');
        const ipPrefix = clientIp.split('.').slice(0, 3).join('.');
        return networkPrefix === ipPrefix;
    };

    // Validasi absensi: GPS (radius) + lokasi user; tampilkan nama + jarak ke lokasi terdekat
    const evaluateAccess = useCallback((lat, lon) => {
        if (lat == null || lon == null) return;

        if (!userLokasi?.length) {
            // GPS sudah ada, lokasi assign belum ke-load — jangan hapus jarak
            setLocationAllowed(false);
            return;
        }

        let nearest = null;
        let nearestDistance = Infinity;
        let matched = null;
        let matchedDistance = Infinity;

        for (const lokasi of userLokasi) {
            const distance = getDistanceFromLatLonInMeters(
                lat,
                lon,
                Number(lokasi.latitude),
                Number(lokasi.longitude)
            );
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearest = lokasi;
            }
            if (distance <= Number(lokasi.radius) && distance < matchedDistance) {
                matchedDistance = distance;
                matched = lokasi;
            }
        }

        const active = matched || nearest;
        setLocationName(active?.nama?.trim() || null);
        setDistanceMeters(matched ? matchedDistance : nearestDistance);
        setLocationAllowed(Boolean(matched));
        setError(matched ? null : 'Anda tidak berada di lokasi yang diizinkan');
    }, [userLokasi]);

    evaluateAccessRef.current = evaluateAccess;

    const getUserLokasi = async () => {
        try {
            const response = await axios.get('/api/lokasi/saya', { withCredentials: true });
            const items = response.data || [];
            setUserLokasi(items);
            // Nama sementara dari assign; diganti ke lokasi terdekat setelah GPS didapat
            if (items.length === 1) {
                setLocationName(items[0].nama?.trim() || null);
            } else if (items.length > 1) {
                setLocationName(items.map((l) => l.nama?.trim()).filter(Boolean).join(', ') || null);
            } else {
                setLocationName(null);
                setError('Belum ada lokasi absensi. Hubungi admin.');
            }
        } catch (err) {
            setError('Gagal memuat lokasi absensi');
        }
    };

    const formatDistance = (meters) => {
        if (meters == null || Number.isNaN(meters)) return null;
        if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
        // 1 desimal supaya pergerakan kecil terlihat
        return `${Number(meters).toFixed(1)} m`;
    };

    /** Nama + jarak dari data absensi tersimpan (bukan GPS live). */
    const getRecordedBadge = (record, kind) => {
        if (!record) return { name: null, distance: null };
        const isMasuk = kind === 'masuk';
        const name = (isMasuk ? record.locationNameMasuk : record.locationNameKeluar)?.trim() || null;
        const lat = Number(isMasuk ? record.latitudeMasuk : record.latitudeKeluar);
        const lon = Number(isMasuk ? record.longitudeMasuk : record.longitudeKeluar);

        let meters = null;
        if (Number.isFinite(lat) && Number.isFinite(lon) && userLokasi?.length) {
            let target = name
                ? userLokasi.find((l) => String(l.nama || '').trim().toLowerCase() === name.toLowerCase())
                : null;
            if (!target) {
                let best = Infinity;
                for (const lokasi of userLokasi) {
                    const d = getDistanceFromLatLonInMeters(
                        lat,
                        lon,
                        Number(lokasi.latitude),
                        Number(lokasi.longitude),
                    );
                    if (d < best) {
                        best = d;
                        target = lokasi;
                    }
                }
            }
            if (target) {
                meters = Math.round(
                    getDistanceFromLatLonInMeters(
                        lat,
                        lon,
                        Number(target.latitude),
                        Number(target.longitude),
                    ),
                );
            }
        }

        return {
            name: name || targetNameFallback(userLokasi),
            distance: formatDistance(meters),
        };
    };

    const targetNameFallback = (list) => {
        if (!list?.length) return 'Lokasi absensi';
        if (list.length === 1) return list[0].nama?.trim() || 'Lokasi absensi';
        return list.map((l) => l.nama?.trim()).filter(Boolean).join(', ') || 'Lokasi absensi';
    };

    const getAbsensiHariIni = async () => {
        try {
            setLoadingAbsensi(true);
            const response = await axios.get(`/api/absensi/dashboard`, {
                withCredentials: true
            });
            const today = response.data?.absensiHariIni || null;
            setAbsensiHariIni(today);
            // Sudah masuk tapi belum pulang → buka tab Absen Pulang
            if (today?.waktuMasuk && !today?.waktuKeluar) {
                setActiveTab(1);
            }
        } catch (error) {
            console.error('Error fetching absensi hari ini:', error);
            setAbsensiHariIni(null);
        } finally {
            setLoadingAbsensi(false);
        }
    };

    const checkHoliday = async () => {
        try {
            const today = new Date();
            const todayStr = formatDate01(today);

            // Check if today is Sunday
            const dayOfWeek = today.getDay();
            if (dayOfWeek === 0) {
                setIsSunday(true);
                setIsHoliday(true);
                setHolidayInfo({ nama: 'Hari Minggu', keterangan: 'Hari libur mingguan' });
                return;
            }

            // Check if today is a holiday
            const dateParam = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const response = await axios.get(`/api/hari-libur/tanggal/${dateParam}`, {
                withCredentials: true
            });

            if (response.data) {
                setIsHoliday(true);
                setHolidayInfo(response.data);
            } else {
                setIsHoliday(false);
                setHolidayInfo(null);
            }
        } catch (error) {
            // If error, assume it's not a holiday
            setIsHoliday(false);
            setHolidayInfo(null);
        }
    };

    const getLocalIP = useCallback(async () => {
        // Prevent multiple simultaneous calls
        if (isGettingIPRef.current || ipClient) {
            return;
        }

        // Limit retries to prevent infinite loop
        if (retryCountRef.current >= 5) {
            setError('Gagal mendapatkan IP lokal setelah beberapa percobaan');
            return;
        }

        isGettingIPRef.current = true;
        retryCountRef.current += 1;

        // Cleanup previous connection if exists
        if (pcRef.current) {
            try {
                pcRef.current.onicecandidate = null;
                pcRef.current.close();
            } catch (e) {
                // Ignore cleanup errors
            }
            pcRef.current = null;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        try {
            const RTCPeerConnection = window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection;
            if (!RTCPeerConnection) {
                setIpClient('WebRTC tidak didukung oleh browser ini');
                isGettingIPRef.current = false;
                return;
            }

            const pc = new RTCPeerConnection({ iceServers: [] });
            pcRef.current = pc;
            pc.createDataChannel('');
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            // Set timeout to prevent hanging
            timeoutRef.current = setTimeout(() => {
                if (pcRef.current) {
                    try {
                        pcRef.current.onicecandidate = null;
                        pcRef.current.close();
                    } catch (e) {
                        // Ignore cleanup errors
                    }
                    pcRef.current = null;
                }
                isGettingIPRef.current = false;
                if (!ipClient && retryCountRef.current < 5) {
                    // Retry after timeout
                    setTimeout(() => getLocalIP(), 2000);
                } else if (!ipClient) {
                    setError('Gagal mendapatkan IP lokal - timeout');
                }
            }, 8000); // 8 second timeout

            pc.onicecandidate = (event) => {
                if (event && event.candidate && event.candidate.candidate) {
                    const ipMatch = /([0-9]{1,3}(\.[0-9]{1,3}){3})/.exec(event.candidate.candidate);
                    if (userLokasi?.length && ipMatch) {
                        const currentIP = ipMatch[1];
                        const anyRequiresNetwork = userLokasi.some((l) => l.jaringan && l.jaringan !== '0');
                        const matchedNetwork = userLokasi.some(
                            (l) => l.jaringan && l.jaringan !== '0' && ipPrefixMatch(currentIP, l.jaringan)
                        );

                        if (matchedNetwork || !anyRequiresNetwork) {
                            setIpClient(currentIP);
                            ipClientRef.current = currentIP;
                            if (timeoutRef.current) {
                                clearTimeout(timeoutRef.current);
                                timeoutRef.current = null;
                            }
                            pc.onicecandidate = null;
                            pc.close();
                            pcRef.current = null;
                            isGettingIPRef.current = false;
                            retryCountRef.current = 0;
                            evaluateAccess(userLocationRef.current.latitude, userLocationRef.current.longitude);
                        } else if (anyRequiresNetwork) {
                            // Simpan IP tetap, biar evaluateAccess kasih pesan jaringan
                            setIpClient(currentIP);
                            ipClientRef.current = currentIP;
                            if (timeoutRef.current) {
                                clearTimeout(timeoutRef.current);
                                timeoutRef.current = null;
                            }
                            pc.onicecandidate = null;
                            pc.close();
                            pcRef.current = null;
                            isGettingIPRef.current = false;
                            evaluateAccess(userLocationRef.current.latitude, userLocationRef.current.longitude);
                        }
                    }
                }
            };
        } catch (error) {
            console.error('Error getting local IP:', error);
            isGettingIPRef.current = false;
            if (retryCountRef.current < 5) {
                // Retry after error
                setTimeout(() => getLocalIP(), 2000);
            } else {
                setError('Gagal mendapatkan IP lokal');
            }
        }
    }, [userLokasi, ipClient, evaluateAccess]);

    const startLocationWatch = useCallback(() => {
        if (!navigator.geolocation) {
            setError('Geolocation tidak didukung oleh browser ini');
            return;
        }

        if (watchIdRef.current != null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
        if (locationPollRef.current != null) {
            clearInterval(locationPollRef.current);
            locationPollRef.current = null;
        }

        // Restore cache supaya jarak langsung tampil saat balik ke halaman
        try {
            const raw = sessionStorage.getItem(GEO_CACHE_KEY);
            if (raw) {
                const cached = JSON.parse(raw);
                const age = Date.now() - (cached.t || 0);
                if (
                    age < 120000 &&
                    cached.latitude != null &&
                    cached.longitude != null
                ) {
                    setUserLocation({
                        latitude: cached.latitude,
                        longitude: cached.longitude,
                    });
                    userLocationRef.current = {
                        latitude: cached.latitude,
                        longitude: cached.longitude,
                    };
                    if (cached.accuracy != null) {
                        setGpsDebug((prev) => ({
                            tick: Math.max(prev.tick, 1),
                            accuracy: cached.accuracy,
                        }));
                    }
                    evaluateAccessRef.current?.(cached.latitude, cached.longitude);
                }
            }
        } catch {
            // ignore cache
        }

        const geoOptions = {
            enableHighAccuracy: true,
            timeout: 60000,
            maximumAge: 5000,
        };

        const applyPosition = (position) => {
            const { latitude, longitude, accuracy } = position.coords;
            setUserLocation({ latitude, longitude });
            userLocationRef.current = { latitude, longitude };
            setGpsDebug((prev) => ({
                tick: prev.tick + 1,
                accuracy: accuracy != null ? Math.round(accuracy) : null,
            }));
            try {
                sessionStorage.setItem(
                    GEO_CACHE_KEY,
                    JSON.stringify({
                        latitude,
                        longitude,
                        accuracy: accuracy != null ? Math.round(accuracy) : null,
                        t: Date.now(),
                    }),
                );
            } catch {
                // ignore
            }
            evaluateAccessRef.current?.(latitude, longitude);
        };

        const onGeoError = (geoError) => {
            console.log('Geolocation error:', geoError);
            if (
                geoError.code === geoError.TIMEOUT ||
                geoError.code === geoError.POSITION_UNAVAILABLE
            ) {
                return;
            }
            if (geoError.code === geoError.PERMISSION_DENIED) {
                setError('Akses lokasi ditolak. Silakan izinkan akses lokasi di pengaturan browser.');
                setLocationAllowed(false);
                if (watchIdRef.current != null) {
                    navigator.geolocation.clearWatch(watchIdRef.current);
                    watchIdRef.current = null;
                }
            }
        };

        navigator.geolocation.getCurrentPosition(applyPosition, onGeoError, geoOptions);
        watchIdRef.current = navigator.geolocation.watchPosition(
            applyPosition,
            onGeoError,
            geoOptions,
        );

        locationPollRef.current = setInterval(() => {
            navigator.geolocation.getCurrentPosition(applyPosition, () => {}, {
                enableHighAccuracy: true,
                timeout: 30000,
                maximumAge: 5000,
            });
        }, 10000);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            await getUserLokasi();
            await checkHoliday();
            await getAbsensiHariIni();
        };
        fetchData();
    }, []);

    // GPS start sekali saat masuk halaman Absensi (tidak nunggu API lokasi)
    useEffect(() => {
        retryCountRef.current = 0;
        isGettingIPRef.current = false;
        startLocationWatch();

        return () => {
            if (watchIdRef.current != null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
            if (locationPollRef.current != null) {
                clearInterval(locationPollRef.current);
                locationPollRef.current = null;
            }
            if (pcRef.current) {
                try {
                    pcRef.current.onicecandidate = null;
                    pcRef.current.close();
                } catch (e) {
                    // Ignore cleanup errors
                }
                pcRef.current = null;
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            isGettingIPRef.current = false;
        };
    }, [startLocationWatch]);

    // Setelah daftar lokasi absensi ke-load, hitung ulang jarak dari GPS terakhir
    useEffect(() => {
        if (!userLokasi?.length) return;
        const { latitude, longitude } = userLocationRef.current;
        if (latitude != null && longitude != null) {
            evaluateAccess(latitude, longitude);
        }
    }, [userLokasi, evaluateAccess]);

    const tabsData = [
        {
            label: 'Absen Masuk',
            type: 'masuk',
            icon: 'FaSignInAlt',
            color: 'green'
        },
        {
            label: 'Absen Pulang',
            type: 'pulang',
            icon: 'FaSignOutAlt',
            color: '[#602829]'
        }
    ];

    return (
        <div className="flex bg-white rounded-lg flex-col w-full items-center md:px-8 h-full min-h-0">

            {isHoliday ? (
                <div className='w-full h-full relative flex flex-col items-center justify-center p-8 flex-1 min-h-0'>
                    <div className=' flex flex-col items-center justify-center bg-red-50 rounded-lg p-8 max-w-md mx-auto border-2 border-red-200'>
                        <FaCalendarTimes className="text-red-600 text-6xl mb-4" />
                        <h2 className="text-2xl font-bold text-red-800 mb-2">Hari Libur</h2>
                        <p className="text-lg font-semibold text-red-700 mb-2">{holidayInfo?.nama || 'Hari Libur'}</p>
                        {holidayInfo?.keterangan && (
                            <p className="text-sm text-gray-600 mb-4">{holidayInfo.keterangan}</p>
                        )}
                        <p className="text-sm text-gray-700 text-center">
                            {isSunday
                                ? 'Hari ini adalah hari Minggu. Absensi tidak dapat dilakukan pada hari libur.'
                                : 'Hari ini adalah hari libur. Absensi tidak dapat dilakukan pada hari libur.'
                            }
                        </p>
                    </div>
                </div>
            ) : (userLocation?.latitude != null && userLocation?.longitude != null)
                || absensiHariIni?.waktuMasuk
                || absensiHariIni?.waktuKeluar ? (
                (() => {
                    const showMasukDone = activeTab === 0 && absensiHariIni?.waktuMasuk;
                    const showPulangDone = activeTab === 1 && absensiHariIni?.waktuKeluar;
                    const outsideLokasi = !locationAllowed;

                    const tabDone = [
                        Boolean(absensiHariIni?.waktuMasuk),
                        Boolean(absensiHariIni?.waktuKeluar),
                    ];

                    const tabsEl = (
                        <div
                            className="relative flex w-full max-w-sm mx-auto rounded-full p-1
                                bg-black/45 backdrop-blur-md border border-white/15 shadow-lg
                                md:bg-white md:border-gray-200/80 md:shadow-md md:backdrop-blur-none"
                            role="tablist"
                            aria-label="Mode absensi"
                        >
                            <div
                                className="absolute top-1 bottom-1 left-1 rounded-full transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] shadow-md"
                                style={{
                                    width: 'calc(50% - 4px)',
                                    transform: `translateX(${activeTab * 100}%)`,
                                    background:
                                        activeTab === 0
                                            ? 'linear-gradient(135deg, #1B3C33 0%, #2d5a4a 100%)'
                                            : 'linear-gradient(135deg, #602829 0%, #7a3436 100%)',
                                }}
                            />
                            {tabsData.map((tab, index) => {
                                const active = activeTab === index;
                                const done = tabDone[index];
                                return (
                                    <button
                                        key={tab.type}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => setActiveTab(index)}
                                        className={`relative z-10 flex-1 min-w-0 px-3 py-2.5 md:py-3 flex items-center justify-center gap-1.5 rounded-full transition-colors duration-300 ${
                                            active
                                                ? 'text-white'
                                                : 'text-white/70 md:text-gray-500 hover:text-white md:hover:text-gray-800'
                                        }`}
                                    >
                                        {done ? (
                                            <span
                                                className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] ${
                                                    active
                                                        ? 'bg-white/25 text-white'
                                                        : 'bg-emerald-500/90 text-white md:bg-emerald-600'
                                                }`}
                                            >
                                                <FaCheck />
                                            </span>
                                        ) : (
                                            <IconRenderer iconStr={tab.icon} />
                                        )}
                                        <span className="text-sm font-semibold tracking-wide">
                                            {index === 0 ? 'Masuk' : 'Pulang'}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    );

                    // Live GPS — hanya untuk kamera aktif
                    const liveLocName =
                        locationName
                        || userLokasi.map((l) => l.nama?.trim()).filter(Boolean).join(', ')
                        || 'Lokasi absensi';
                    const liveLocDistance = formatDistance(distanceMeters);

                    // Setelah absen: pakai lokasi/jarak dari record tersimpan
                    const recordedMasuk = getRecordedBadge(absensiHariIni, 'masuk');
                    const recordedPulang = getRecordedBadge(absensiHariIni, 'keluar');

                    const hasLiveGps =
                        userLocation?.latitude != null && userLocation?.longitude != null;
                    const cameraProps = {
                        onCapture: handleCapture,
                        onError: handleError,
                        ip_client: ipClient,
                        type: tabsData[activeTab].type,
                        location: userLocation,
                        locationName: liveLocName,
                        distanceLabel: liveLocDistance,
                        coords: userLocation,
                        gpsDebug,
                        captureDisabled: outsideLokasi || !hasLiveGps,
                        // Error lokasi TETAP tampil; error GPS timeout tidak dipakai di sini
                        statusMessage: outsideLokasi
                            ? 'Anda tidak berada di lokasi yang diizinkan'
                            : (error?.includes('Akses lokasi ditolak') ? error : null),
                    };

                    const donePhotoView = (foto, waktu, waktuLabel, statusText, badge) => (
                        <div className="relative w-full h-full bg-black">
                            {foto ? (
                                <AuthImage
                                    src={foto}
                                    alt={statusText}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                                    Foto tidak tersedia
                                </div>
                            )}

                            <LocationBadge
                                name={badge?.name}
                                distance={badge?.distance}
                                top="4.25rem"
                            />

                            {waktu && (
                                <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                    <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg border border-white/20">
                                        <div className="text-center">
                                            <div className="text-xs text-white/80 mb-1">{waktuLabel}</div>
                                            <div className="text-lg font-bold">{formatTime(waktu)}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="absolute left-0 right-0 z-40 flex justify-center px-4 bottom-[calc(6.25rem+env(safe-area-inset-bottom,0px))] md:bottom-10">
                                <div className="bg-black/55 text-white text-sm font-medium px-4 py-2 rounded-full">
                                    {statusText}
                                </div>
                            </div>
                        </div>
                    );

                    return (
                        /* Fullscreen kamera: mobile di bawah header; desktop isi area kanan (samping sidebar) */
                        <div className="fixed z-30 bg-black inset-x-0 bottom-0 top-12 md:left-64 md:top-[4.75rem] md:bottom-0">
                            <div className="absolute left-0 right-0 z-40 flex justify-center px-3 md:px-6 pointer-events-none top-3 md:top-5">
                                <div className="pointer-events-auto w-full max-w-sm md:max-w-md">
                                    {tabsEl}
                                </div>
                            </div>

                            {showMasukDone
                                ? donePhotoView(
                                    absensiHariIni.fotoMasuk,
                                    absensiHariIni.waktuMasuk,
                                    'Waktu Absen Masuk',
                                    'Anda sudah melakukan absen masuk',
                                    recordedMasuk,
                                )
                                : showPulangDone
                                    ? donePhotoView(
                                        absensiHariIni.fotoKeluar,
                                        absensiHariIni.waktuKeluar,
                                        'Waktu Absen Pulang',
                                        'Anda sudah melakukan absen pulang',
                                        recordedPulang,
                                    )
                                    : <CameraCapture {...cameraProps} fullscreen />
                            }
                        </div>
                    );
                })()
            ) : (
                // <div className="w-full min-h-screen -mt-20 flex justify-center items-center rounded-md" style={{ filter: "drop-shadow(0px 0px 9px rgba(0,0,0,0.18))" }}>
                //     <div className="flex flex-col p-8 mx-auto max-w-7xl xl:p-12">
                //         <div className="flex pt-4 rounded-lg flex-col items-center h-full">
                //             <button onClick={() => window.location.reload()} className="w-56 flex items-center px-4 m-4 py-2 shadow-lg shadow-gray-500/50 bg-red-600 text-white rounded-lg text-[15px] cursor-pointer active:scale-[.97]">
                //                 <span className='text-4xl mr-2'><FaUndoAlt /></span>
                //                 <span>{error ? error : "Mencari Lokasi.."}</span>
                //             </button>
                //         </div>
                //     </div>
                // </div>
                <div className='w-full h-full relative flex flex-col items-center justify-center flex-1 min-h-0'>
                    {/* Loading Section */}
                    <div className='flex flex-col items-center justify-center'>

                        <div className="text-center text-gray-600 text-sm md:text-base">
                            <div className="mb-4">
                                <svg className="animate-spin h-12 w-12 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        </div>
                        <p className="font-medium mb-2">Mencari lokasi anda...</p>
                        <p className="text-xs text-gray-500">Mohon tunggu sebentar</p>
                        <button onClick={() => window.location.reload()} className=" gap-2 flex items-center px-4 m-4 py-2 shadow-lg shadow-gray-500/50 bg-yellow-600 text-white rounded-lg text-[15px] cursor-pointer active:scale-[.97]">
                            <span className=''><FaUndoAlt /></span>
                            <span>{error ? error : "Reload"}</span>
                        </button>
                    </div>
                </div>
            )
            }
        </div >
    );
};

export default Absensi;